// Contexto de sessão. Guarda o token e o usuário, e recarrega /auth/me no
// início para o caso de o token ainda valer de uma visita anterior.
//
// A sessão tem estado no backend: quando o usuário é desativado ou troca a
// senha, a próxima chamada volta 401 e o `setSessionExpiredHandler` do cliente
// HTTP limpa tudo — o front desloga sozinho, sem esperar o token expirar.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import type { LoginInput, LoginResponse, SessionUser } from '@c1hd/shared'
import { api, getToken, setSessionExpiredHandler, setToken } from './api'

interface AuthState {
  user: SessionUser | null
  loading: boolean
  /** `mustChangePassword` true quando a conta exige trocar a senha antes de
   *  entrar; nesse caso `user` vem nulo. */
  login: (
    input: LoginInput,
  ) => Promise<{ mustChangePassword: boolean; user: SessionUser | null }>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setSessionExpiredHandler(() => setUser(null))
  }, [])

  useEffect(() => {
    let cancelled = false
    async function restore() {
      if (!getToken()) {
        setLoading(false)
        return
      }
      try {
        const me = await api.get<SessionUser>('/auth/me')
        if (!cancelled) setUser(me)
      } catch {
        setToken(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [])

  const login = useCallback(async (input: LoginInput) => {
    const res = await api.post<LoginResponse | { mustChangePassword: true }>(
      '/auth/login',
      input,
    )
    if ('mustChangePassword' in res) return { mustChangePassword: true, user: null }
    setToken(res.token)
    setUser(res.user)
    return { mustChangePassword: false, user: res.user }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } finally {
      setToken(null)
      setUser(null)
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>')
  return ctx
}
